'use client';

import type { CSSProperties, MouseEvent, ReactNode } from 'react';
import { adaptProductCardToCart } from '../cart/cart-utils';
import { useAddToCart, useProductInventory } from '../cart/hooks';
import type { ProductInventoryResponse } from '../cart/queries/get-product-inventory';
import type { ElevarImpression } from '../elevar/elevar-types';
import { useMaxQuantity } from '../lib/hooks/use-max-quantity';
import { cn, formatPrice } from '../lib/utils';
import { SanityImage } from '../sanity/utils/components';
import type { SanityImageProps } from '../sanity/utils/fragments/image-fragment';
import { CustomLink } from './custom-link';

export interface SharedProductCardData {
  title: string;
  slug: string;
  productGid: string | null;
  variantGid: string | null;
  price: number;
  compareAtPrice?: number | null;
  sku?: string | null;
  category?: string;
  allowBackorder: boolean | null;
  mainImage?: SanityImageProps | null;
  hoverImage?: SanityImageProps | null;
  size: string | null;
  squareMetersPerBox: number | null;
  fileDownloadURL?: string | null;
  preOrderTag?: string | null;
}

function ProductCardRoot({
  product,
  children,
  onSelectItem,
  className,
  elevarImpression,
}: {
  product: SharedProductCardData;
  index: number;
  children: ReactNode;
  onSelectItem?: () => void;
  className?: string;
  elevarImpression?: ElevarImpression;
}) {
  const isToolsAndResources = product.category === 'TOOLS & RESOURCES';
  const hasDownloadUrl = !!product.fileDownloadURL;

  // For tools & resources with download URLs, we need a different structure
  if (isToolsAndResources && hasDownloadUrl) {
    return (
      <article
        className={cn(
          'relative isolate flex h-full flex-col items-stretch space-y-1 overflow-hidden rounded-theme-lg text-brand-burgundy',
          className
        )}
        data-elevar-impression={
          elevarImpression ? JSON.stringify(elevarImpression) : undefined
        }
      >
        {children}
      </article>
    );
  }

  // For regular products, wrap in a link
  return (
    <article
      className={cn(
        'relative isolate flex h-full flex-col items-stretch space-y-1 overflow-hidden rounded-theme-lg text-brand-burgundy',
        className
      )}
      data-elevar-impression={
        elevarImpression ? JSON.stringify(elevarImpression) : undefined
      }
    >
      <CustomLink
        href={`/products/${product.slug}`}
        onClick={onSelectItem}
        className="flex h-full flex-col"
      >
        {children}
      </CustomLink>
    </article>
  );
}

function ProductCardImageContainer({
  product,
  children,
  className,
  style,
}: {
  product: SharedProductCardData;
  children?: ReactNode;
  className?: string;
  style?: CSSProperties;
}) {
  const isToolsAndResources = product.category === 'TOOLS & RESOURCES';
  const hasDownloadUrl = !!product.fileDownloadURL;

  const imageContent = (
    <>
      {children}
      <div className="absolute inset-0 h-full w-full overflow-hidden">
        {product.mainImage && (
          <SanityImage
            image={product.mainImage}
            fill
            className="product-card-image inset-0 h-full w-full select-none object-cover drop-shadow-product"
            sizes="(max-width: 768px) 50vw, (max-width: 1024px) 33vw, 25vw"
          />
        )}
      </div>
      {product.hoverImage && (
        <div className="absolute inset-0 h-full w-full overflow-hidden hf:opacity-100 opacity-0 transition-all duration-75">
          <SanityImage
            image={product.hoverImage}
            fill
            className="product-card-image inset-0 h-full w-full select-none object-cover drop-shadow-product"
            sizes="(max-width: 768px) 50vw, (max-width: 1024px) 33vw, 25vw"
          />
        </div>
      )}
    </>
  );

  // For tools & resources, wrap the image in a link
  if (isToolsAndResources && hasDownloadUrl) {
    return (
      <CustomLink
        href={`/products/${product.slug}`}
        className={cn(
          'product-card__image relative grid h-0 pb-[100%] hf:outline-none outline-none',
          className
        )}
        style={{ marginBottom: '12px', ...style }}
      >
        {imageContent}
      </CustomLink>
    );
  }

  return (
    <div
      className={cn(
        'product-card__image relative grid h-0 pb-[100%] hf:outline-none outline-none',
        className
      )}
      style={{ marginBottom: '12px', ...style }}
    >
      {imageContent}
    </div>
  );
}

function ProductCardContent({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'relative flex h-full flex-1 flex-col items-stretch justify-between px-3 pb-3',
        className
      )}
    >
      {children}
    </div>
  );
}

function ProductCardHeader({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return <div className={cn('grid gap-1', className)}>{children}</div>;
}

function ProductCardTitle({
  product,
  className,
}: {
  product: SharedProductCardData;
  className?: string;
}) {
  const isToolsAndResources = product.category === 'TOOLS & RESOURCES';
  const hasDownloadUrl = !!product.fileDownloadURL;

  // For tools & resources, wrap the title in a link
  if (isToolsAndResources && hasDownloadUrl) {
    return (
      <CustomLink
        href={`/products/${product.slug}`}
        className={cn(
          'font-bold hf:underline hf:outline-none outline-none',
          className
        )}
      >
        {product.title}
      </CustomLink>
    );
  }

  return (
    <span
      className={cn(
        'font-bold hf:underline hf:outline-none outline-none',
        className
      )}
    >
      {product.title}
    </span>
  );
}

function ProductCardFooter({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return <div className={className}>{children}</div>;
}

function ProductCardDimensions({
  dimensions,
  className,
}: {
  dimensions: string;
  className?: string;
}) {
  return (
    <span className={cn('font-normal', className)}>
      <b className="text-[hsl(var(--color))]">{dimensions}</b> mm
    </span>
  );
}

function ProductCardPricing({
  product,
  containerClassName,
  priceClassName,
  compareAtPriceClassName,
}: {
  product: SharedProductCardData;
  containerClassName?: string;
  priceClassName?: string;
  compareAtPriceClassName?: string;
}) {
  const correctCompareAtPrice =
    product.compareAtPrice !== 0 ? product.compareAtPrice : null;

  const isTile = product.category === 'TILE';
  const unit = isTile ? '/ m²' : '';
  const squareMetersPerBox = product.squareMetersPerBox || 1;

  const displayPrice = isTile
    ? product.price / squareMetersPerBox
    : product.price;
  const displayCompareAtPrice =
    correctCompareAtPrice && isTile
      ? correctCompareAtPrice / squareMetersPerBox
      : correctCompareAtPrice;

  return (
    <div
      className={cn(
        'flex items-baseline justify-end gap-1.5',
        containerClassName
      )}
    >
      <div className="flex items-baseline gap-1.5">
        {displayCompareAtPrice && displayCompareAtPrice > displayPrice && (
          <span
            className={cn(
              'font-normal text-gray-500 line-through',
              compareAtPriceClassName
            )}
          >
            <b>{formatPrice(displayCompareAtPrice)}</b>
          </span>
        )}
        <span className={cn('font-normal', priceClassName)}>
          <b>{formatPrice(displayPrice)}</b>
          {unit && <> {unit}</>}
        </span>
      </div>
    </div>
  );
}

function ProductCardPriceRow({
  product,
  className,
}: {
  product: SharedProductCardData;
  className?: string;
}) {
  const size = product.size;
  const isTile = product.category === 'TILE';
  const hasDimensions = isTile && size;
  const price = product.price;
  const compareAtPrice = product.compareAtPrice ?? null;

  const correctCompareAtPrice = compareAtPrice !== 0 ? compareAtPrice : null;

  const hasDiscount =
    correctCompareAtPrice &&
    correctCompareAtPrice > 0 &&
    correctCompareAtPrice > product.price;
  const squareMetersPerBox = product.squareMetersPerBox || null;
  const chosenCompareAtPrice = correctCompareAtPrice ?? null;

  return (
    <div className={cn('responsive flex justify-between gap-3', className)}>
      <div className="flex w-full justify-between gap-2 whitespace-nowrap">
        <div
          className={cn(
            'flex w-full items-baseline gap-1.5',
            hasDimensions ? 'justify-between' : 'justify-end'
          )}
        >
          {hasDimensions && <ProductCardDimensions dimensions={size} />}

          <div className="col-span-full">
            <div className="grid auto-cols-min grid-flow-col-dense items-baseline gap-2 whitespace-nowrap font-bold text-[hsl(var(--color))]">
              <div>
                {squareMetersPerBox ? (
                  <>
                    {hasDiscount && (
                      <span className="mr-2 inline-block text-gray-500 line-through">
                        ${chosenCompareAtPrice?.toFixed(0)}
                      </span>
                    )}
                    <span className="inline-block">
                      ${Number((price / squareMetersPerBox).toFixed(0))}
                    </span>
                    /m<sup>2</sup>
                  </>
                ) : (
                  <>
                    {hasDiscount && (
                      <span className="mr-2 inline-block text-gray-500 line-through">
                        ${chosenCompareAtPrice?.toFixed(0)}
                      </span>
                    )}
                    <span className="inline-block">${price.toFixed(0)}</span>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ProductCardAddToCartButton({
  product,
  domain,
  buttonClassName,
  containerClassName,
  addToCartText = 'Add to cart',
  addText = 'Add',
  soldOutText = 'Sold out',
}: {
  product: SharedProductCardData;
  domain: string;
  buttonClassName?: string;
  containerClassName?: string;
  addToCartText?: string;
  addText?: string;
  soldOutText?: string;
}) {
  const { addItem } = useAddToCart({ domain });
  const { data: inventory, isLoading: isInventoryLoading } =
    useProductInventory(product.productGid);
  const maxQuantityAvailable = useMaxQuantity({
    productGid: product.productGid,
    variantGid: product.variantGid,
    allowBackorder: product.allowBackorder ?? false,
  });
  const isToolsAndResources = product.category === 'TOOLS & RESOURCES';
  const canBackorder = product.allowBackorder ?? false;
  const isPreOrder = canBackorder && !!product.preOrderTag;

  // For button disabled state - use the actual inventory data
  const isDisabled =
    inventory !== undefined && !inventory?.availableForSale && !isPreOrder;

  // For display text - only show "Sold out" when we're sure (not during SSR/loading)
  const showSoldOut =
    !isInventoryLoading &&
    inventory !== undefined &&
    !inventory?.availableForSale &&
    !isPreOrder;

  if (isToolsAndResources && product.fileDownloadURL) {
    return (
      <div
        className={cn(
          'mt-auto flex w-full flex-1 flex-col justify-end self-end text-center',
          containerClassName
        )}
      >
        <a
          href={product.fileDownloadURL}
          target="_blank"
          rel="noopener noreferrer"
          className={cn(
            'mt-2 inline-flex w-full items-center justify-center',
            buttonClassName
          )}
        >
          Free Download
        </a>
      </div>
    );
  }

  const handleAddToCart = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!product.variantGid || !product.productGid) return;

    const { product: cartProduct, variant: cartVariant } =
      adaptProductCardToCart({
        ...product,
        variantGid: product.variantGid,
        productGid: product.productGid,
        category: product.category || 'Unknown',
        allowBackorder: canBackorder,
        preOrderTag: product.preOrderTag,
      });

    addItem({
      product: cartProduct,
      variant: cartVariant,
      quantity: 1,
      wastageMultiplier: 1, // Default wastage for product cards
      maxQuantityAvailable,
      productUrl:
        product.category === 'SAMPLE' ? `/products/${product.slug}` : undefined,
    });
  };

  return (
    <div
      className={cn(
        'mt-auto flex w-full flex-1 flex-col justify-end self-end text-center',
        containerClassName
      )}
    >
      <button
        onClick={handleAddToCart}
        disabled={isDisabled}
        className={cn('mt-2', buttonClassName)}
        type="button"
      >
        {isPreOrder && <span>Pre-order</span>}
        {showSoldOut && <span>{soldOutText}</span>}
        {!isPreOrder && !showSoldOut && (
          <>
            <span className="max-md:hidden">{addToCartText}</span>
            <span className="md:hidden">{addText}</span>
          </>
        )}
      </button>
    </div>
  );
}

function ProductCardInventory({
  productGid,
  children,
}: {
  productGid: string | null;
  children: (data: {
    inventory: ProductInventoryResponse;
    isLoading: boolean;
    isOutOfStock: boolean;
  }) => ReactNode;
}) {
  const {
    data: inventory,
    isLoading: isInventoryLoading,
    isOutOfStock,
  } = useProductInventory(productGid);

  return (
    <>
      {children({
        inventory: inventory || null,
        isLoading: isInventoryLoading,
        isOutOfStock,
      })}
    </>
  );
}

// Compound component structure
ProductCardRoot.ImageContainer = ProductCardImageContainer;
ProductCardRoot.Content = ProductCardContent;
ProductCardRoot.Header = ProductCardHeader;
ProductCardRoot.Title = ProductCardTitle;
ProductCardRoot.Footer = ProductCardFooter;
ProductCardRoot.Dimensions = ProductCardDimensions;
ProductCardRoot.Pricing = ProductCardPricing;
ProductCardRoot.PriceRow = ProductCardPriceRow;
ProductCardRoot.AddToCartButton = ProductCardAddToCartButton;
ProductCardRoot.Inventory = ProductCardInventory;

export const ProductCard = ProductCardRoot;
